import { useStore } from 'jotai/react';
import { memo, useCallback, useEffect, useRef } from 'react';
import { stageRefAtom } from '@/store/boardAtoms';
const getGridLevels = (gridSize, zoom) => {
    const levels = [];
    if (zoom > 0.2 && zoom < 0.5) {
        levels.push({ step: gridSize * 2, stroke: '#FFFFFF0B', strokeWidth: 1 });
    }
    if (zoom > 0.5) {
        levels.push({ step: gridSize, stroke: '#FFFFFF07', strokeWidth: 1 });
        levels.push({ step: gridSize * 2, stroke: '#FFFFFF07', strokeWidth: 1 });
    }
    if (zoom >= 1) {
        levels.push({ step: gridSize / 2, stroke: '#FFFFFF07', strokeWidth: 1 });
    }
    if (zoom >= 2) {
        levels.push({ step: gridSize / 4, stroke: '#FFFFFF07', strokeWidth: 1 });
    }
    if (zoom >= 4) {
        levels.push({ step: gridSize / 8, stroke: '#FFFFFF07', strokeWidth: 1 });
    }
    // if (zoom >= 16) {
    //   levels.push({ step: gridSize / 16, stroke: '#FFFFFF07', strokeWidth: 1 });
    // }
    return levels;
};
const GridLayer = ({ showGrid, gridSize, zoom, stageSize }) => {
    const store = useStore();
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    // create and attach overlay once — INSERT BEFORE first child so it sits UNDER Konva canvases
    useEffect(() => {
        if (!showGrid)
            return;
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const container = stage.container();
        if (!container)
            return;
        // already attached and in correct parent
        if (canvasRef.current && canvasRef.current.parentElement === container)
            return;
        const canvas = document.createElement('canvas');
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.pointerEvents = 'none';
        // z-index not needed; we insert before Konva canvases to be underneath
        canvas.style.zIndex = '0';
        // make sure container can hold absolutely positioned child
        if (getComputedStyle(container).position === 'static')
            container.style.position = 'relative';
        // insert before first child -> puts overlay under Konva canvases,
        // fallback to appendChild if container is empty
        if (container.firstChild)
            container.insertBefore(canvas, container.firstChild);
        else
            container.appendChild(canvas);
        canvasRef.current = canvas;
        return () => {
            // cleanup if component unmounts before showGrid toggles
            if (canvasRef.current && canvasRef.current.parentElement === container) {
                container.removeChild(canvasRef.current);
                canvasRef.current = null;
            }
        };
    }, [showGrid, store]);
    // draw function (fast, simple)
    const draw = useCallback(() => {
        var _a, _b;
        const canvas = canvasRef.current;
        const stage = store.get(stageRefAtom);
        if (!showGrid || !canvas || !stage) {
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx)
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            return;
        }
        const dpr = window.devicePixelRatio || 1;
        const cssW = Math.max(1, Math.round(stageSize.width));
        const cssH = Math.max(1, Math.round(stageSize.height));
        canvas.width = Math.round(cssW * dpr);
        canvas.height = Math.round(cssH * dpr);
        canvas.style.width = `${cssW}px`;
        canvas.style.height = `${cssH}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        // reset and scale for DPR
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.scale(dpr, dpr);
        // stage translation in screen pixels
        const stageX = (_a = stage.x()) !== null && _a !== void 0 ? _a : 0;
        const stageY = (_b = stage.y()) !== null && _b !== void 0 ? _b : 0;
        const levels = getGridLevels(gridSize, zoom);
        for (const level of levels) {
            const stepPx = level.step * zoom;
            if (!isFinite(stepPx) || stepPx <= 0.0001)
                continue;
            ctx.beginPath();
            ctx.strokeStyle = level.stroke;
            ctx.lineWidth = Math.max(0.5, level.strokeWidth);
            // offset so lines move with stage.x/stage.y
            const offsetX = ((stageX % stepPx) + stepPx) % stepPx;
            const offsetY = ((stageY % stepPx) + stepPx) % stepPx;
            // vertical lines
            for (let x = offsetX - stepPx; x <= cssW + stepPx; x += stepPx) {
                const px = Math.round(x);
                ctx.moveTo(px, 0);
                ctx.lineTo(px, cssH);
            }
            // horizontal lines
            for (let y = offsetY - stepPx; y <= cssH + stepPx; y += stepPx) {
                const py = Math.round(y);
                ctx.moveTo(0, py);
                ctx.lineTo(cssW, py);
            }
            ctx.stroke();
            ctx.closePath();
        }
    }, [showGrid, store, stageSize.width, stageSize.height, gridSize, zoom]);
    // Throttle для оптимизации частых обновлений при панорамировании
    const throttleRef = useRef(null);
    const lastDrawTimeRef = useRef(0);
    const THROTTLE_MS = 5;
    // schedule draw with RAF to batch rapid events + throttling для pan/zoom
    const scheduleDraw = useCallback((immediate = false) => {
        if (rafRef.current != null)
            cancelAnimationFrame(rafRef.current);
        const now = performance.now();
        const timeSinceLastDraw = now - lastDrawTimeRef.current;
        // Для немедленных обновлений (resize, zoom change) - рисуем сразу
        if (immediate || timeSinceLastDraw >= THROTTLE_MS) {
            rafRef.current = requestAnimationFrame(() => {
                draw();
                lastDrawTimeRef.current = performance.now();
                rafRef.current = null;
            });
        }
        else {
            // Для частых обновлений (pan) - используем throttling
            if (throttleRef.current != null) {
                clearTimeout(throttleRef.current);
            }
            throttleRef.current = window.setTimeout(() => {
                rafRef.current = requestAnimationFrame(() => {
                    draw();
                    lastDrawTimeRef.current = performance.now();
                    rafRef.current = null;
                });
                throttleRef.current = null;
            }, THROTTLE_MS - timeSinceLastDraw);
        }
    }, [draw]);
    // listen stage events (pan/position changes) and window resize
    useEffect(() => {
        const stage = store.get(stageRefAtom);
        if (!stage) {
            scheduleDraw(true);
            return;
        }
        // Для частых событий (pan) используем throttled версию
        const onPanChange = () => scheduleDraw(false);
        // Для редких событий (resize, zoom) используем immediate версию
        const onImmediateChange = () => scheduleDraw(true);
        // перерисовываем сетку при измнении позиции сцены
        stage.on('dragmove', onPanChange);
        stage.on('dragend', onImmediateChange);
        stage.on('xChange', onPanChange);
        stage.on('yChange', onPanChange);
        window.addEventListener('resize', onImmediateChange);
        // initial draw
        scheduleDraw(true);
        return () => {
            stage.off('dragmove', onPanChange);
            stage.off('dragend', onImmediateChange);
            stage.off('xChange', onPanChange);
            stage.off('yChange', onPanChange);
            window.removeEventListener('resize', onImmediateChange);
            if (rafRef.current != null)
                cancelAnimationFrame(rafRef.current);
            if (throttleRef.current != null)
                clearTimeout(throttleRef.current);
        };
    }, [store, scheduleDraw]);
    // redraw when props that affect appearance change (immediate для критичных изменений)
    useEffect(() => {
        scheduleDraw(true);
    }, [zoom, gridSize, stageSize.width, stageSize.height, showGrid, scheduleDraw]);
    // remove overlay when grid is hidden
    useEffect(() => {
        if (showGrid)
            return;
        const stage = store.get(stageRefAtom);
        const canvas = canvasRef.current;
        if (canvas && stage) {
            const container = stage.container();
            if (canvas.parentElement === container)
                container.removeChild(canvas);
            canvasRef.current = null;
        }
    }, [showGrid, store]);
    return null;
};
export default memo(GridLayer, (prevProps, nextProps) => {
    // Сравниваем только релевантные свойства, которые влияют на отрисовку
    return (prevProps.showGrid === nextProps.showGrid &&
        prevProps.gridSize === nextProps.gridSize &&
        prevProps.zoom === nextProps.zoom &&
        prevProps.stageSize.width === nextProps.stageSize.width &&
        prevProps.stageSize.height === nextProps.stageSize.height &&
        true // stageRef больше не используется, всегда считаем равным
    );
});
